import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Layout from '../../components/Layout/Layout';
import { Card, Button, Select, Spinner, Badge } from '../../components/UI';
import { dashboardAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  HiOutlineSparkles, HiOutlinePhone, HiOutlineClock, HiOutlineCheck,
  HiOutlineUsers, HiOutlineCalendar, HiOutlineTrendingUp, HiOutlineRefresh
} from 'react-icons/hi';

const Leaderboard = () => {
  const [period, setPeriod] = useState('today');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [sortBy, setSortBy] = useState('totalCalls');

  // Fetch leaderboard data
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['leaderboard', period, customStartDate, customEndDate, sortBy],
    queryFn: async () => {
      try {
        const params = { period, sortBy };
        if (period === 'custom' && customStartDate && customEndDate) {
          params.startDate = customStartDate;
          params.endDate = customEndDate;
        }

        // Debug log to verify params
        console.log('Fetching leaderboard with params:', params);

        const response = await dashboardAPI.getLeaderboard(params);
        return response.data;
      } catch (error) {
        console.error('Leaderboard error:', error);
        toast.error('Failed to load leaderboard');
        return { data: [], summary: {}, allUsers: [] };
      }
    },
    refetchOnWindowFocus: false,
    staleTime: 0, // Always fetch fresh data
  });

  const leaderboard = data?.data || [];
  const summary = data?.summary || {};

  // Handle refresh with toast notification
  const handleRefresh = async () => {
    await refetch();
    toast.success('Leaderboard refreshed');
  };

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
    if (newPeriod !== 'custom') {
      setShowDatePicker(false);
      // Clear custom dates when switching away from custom
      setCustomStartDate('');
      setCustomEndDate('');
    }
  };

  const handleDateRangeConfirm = () => {
    if (customStartDate && customEndDate) {
      // Validate that end date is not before start date
      if (new Date(customEndDate) < new Date(customStartDate)) {
        toast.error('End date cannot be before start date');
        return;
      }
      setPeriod('custom');
      setShowDatePicker(false);
      // Force refetch after setting custom dates
      setTimeout(() => refetch(), 0);
    } else {
      toast.error('Please select both start and end dates');
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '0';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (hrs > 0) return `${hrs}h ${mins}m`;
    if (mins > 0) return `${mins}m ${secs}s`;
    return `${secs}s`;
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return { color: 'bg-yellow-500', icon: '🥇', label: 'Gold' };
    if (rank === 2) return { color: 'bg-gray-400', icon: '🥈', label: 'Silver' };
    if (rank === 3) return { color: 'bg-amber-600', icon: '🥉', label: 'Bronze' };
    return null;
  };

  const getProgressWidth = (value, max) => {
    if (!max || max === 0) return 0;
    return Math.min((value / max) * 100, 100);
  };

  const maxCalls = Math.max(...leaderboard.map(u => u.totalCalls), 1);
  const maxConnected = Math.max(...leaderboard.map(u => u.connectedCalls), 1);

  return (
    <Layout>
      <div className="p-4 lg:p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <HiOutlineSparkles className="w-7 h-7 text-yellow-500" />
              Leaderboard
            </h1>
            <p className="text-gray-500 text-sm mt-1">Telecaller performance rankings</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Refresh Button */}
            <Button
              variant="outline"
              size="sm"
              icon={<HiOutlineRefresh className="w-4 h-4" />}
              onClick={handleRefresh}
              loading={isFetching}
            >
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            {/* Period Filter */}
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              {['today', 'week', 'month'].map((p) => (
                <button
                  key={p}
                  onClick={() => handlePeriodChange(p)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                    period === p
                      ? 'bg-white text-primary-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {p === 'today' ? 'Today' : p === 'week' ? 'This Week' : 'This Month'}
                </button>
              ))}
            </div>
            <Button
              variant={period === 'custom' ? 'primary' : 'outline'}
              size="sm"
              icon={<HiOutlineCalendar className="w-4 h-4" />}
              onClick={() => setShowDatePicker(!showDatePicker)}
            >
              Custom
            </Button>
            {/* Sort By Dropdown */}
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              options={[
                { value: 'totalCalls', label: 'Total Calls' },
                { value: 'connectedCalls', label: 'Connected Calls' },
                { value: 'totalDuration', label: 'Call Duration' },
                { value: 'conversionCount', label: 'Converted Leads' }
              ]}
              className="w-40"
            />
          </div>
        </div>

        {/* Date Picker */}
        {showDatePicker && (
          <Card padding="p-4" className="relative">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-gray-900">Select Date Range</h3>
              <button
                onClick={() => setShowDatePicker(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                &times;
              </button>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 items-end">
              <div className="flex-1">
                <label className="block text-sm text-gray-600 mb-1">Start Date</label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  max={customEndDate || new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm text-gray-600 mb-1">End Date</label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  min={customStartDate}
                  max={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => setShowDatePicker(false)}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleDateRangeConfirm}>
                  Apply
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Summary Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <Card padding="p-4" className="bg-gradient-to-br from-blue-50 to-blue-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500 rounded-lg">
                <HiOutlineUsers className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-blue-600">Active Users</p>
                <p className="text-xl font-bold text-blue-300">{summary?.activeUsers || 0}</p>
              </div>
            </div>
          </Card>
          <Card padding="p-4" className="bg-gradient-to-br from-primary-50 to-primary-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-500 rounded-lg">
                <HiOutlinePhone className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-primary-600">Total Calls</p>
                <p className="text-xl font-bold text-primary-300">{summary?.totalCalls || 0}</p>
              </div>
            </div>
          </Card>
          <Card padding="p-4" className="bg-gradient-to-br from-green-50 to-green-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500 rounded-lg">
                <HiOutlineCheck className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-green-600">Connected</p>
                <p className="text-xl font-bold text-green-900">{summary?.totalConnected || 0}</p>
              </div>
            </div>
          </Card>
          <Card padding="p-4" className="bg-gradient-to-br from-purple-50 to-purple-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500 rounded-lg">
                <HiOutlineClock className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-purple-600">Talk Time</p>
                <p className="text-xl font-bold text-purple-300">{formatDuration(summary?.totalDuration || 0)}</p>
              </div>
            </div>
          </Card>
          <Card padding="p-4" className="bg-gradient-to-br from-amber-50 to-amber-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500 rounded-lg">
                <HiOutlineTrendingUp className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-amber-600">Conversions</p>
                <p className="text-xl font-bold text-amber-900">{summary?.totalConversions || 0}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Period Label */}
        <div className="text-sm text-gray-500 flex items-center gap-2">
          <HiOutlineCalendar className="w-4 h-4" />
          {period === 'today' && "Today's Performance"}
          {period === 'week' && "This Week's Performance"}
          {period === 'month' && "This Month's Performance"}
          {period === 'custom' && customStartDate && customEndDate && (
            <>
              {new Date(customStartDate).toLocaleDateString()} - {new Date(customEndDate).toLocaleDateString()}
            </>
          )}
        </div>

        {/* Leaderboard Table */}
        {(isLoading || isFetching) ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : leaderboard.length === 0 ? (
          <Card>
            <div className="flex flex-col items-center py-12 text-center">
              <HiOutlineSparkles className="w-16 h-16 text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Data Available</h3>
              <p className="text-gray-500">
                No telecaller activity recorded for the selected period
              </p>
            </div>
          </Card>
        ) : (
          <div className="space-y-3">
            {leaderboard.map((user, index) => {
              const rankBadge = getRankBadge(user.rank);
              const isTopThree = user.rank <= 3;

              return (
                <Card
                  key={user.userId}
                  padding="p-0"
                  className={`overflow-hidden transition-all ${
                    isTopThree ? 'ring-2 ring-primary-200' : ''
                  }`}
                >
                  <div className={`flex flex-col sm:flex-row ${isTopThree ? 'bg-gradient-to-r from-primary-50 to-white' : ''}`}>
                    {/* Rank Section */}
                    <div className={`
                      flex items-center justify-center
                      ${isTopThree ? 'sm:w-20 bg-gradient-to-b from-primary-100 to-primary-50' : 'sm:w-16 bg-gray-50'}
                      p-4 sm:p-0
                    `}>
                      {rankBadge ? (
                        <div className="text-center">
                          <span className="text-3xl">{rankBadge.icon}</span>
                          <p className="text-xs font-medium text-gray-600 mt-1">#{user.rank}</p>
                        </div>
                      ) : (
                        <span className="text-xl font-bold text-gray-400">#{user.rank}</span>
                      )}
                    </div>

                    {/* User Info & Stats */}
                    <div className="flex-1 p-4">
                      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                        {/* User Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3">
                            <div className={`
                              w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold
                              ${isTopThree ? 'bg-gradient-to-br from-primary-500 to-primary-600' : 'bg-gray-400'}
                            `}>
                              {user.userName?.charAt(0)?.toUpperCase() || 'U'}
                            </div>
                            <div className="min-w-0">
                              <h3 className="font-semibold text-gray-900 truncate">{user.userName}</h3>
                              <p className="text-sm text-gray-500">{user.email}</p>
                            </div>
                            {user.department && (
                              <Badge variant="default" size="sm">{user.department}</Badge>
                            )}
                          </div>
                        </div>

                        {/* Stats Grid */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4 flex-shrink-0">
                          <div className="text-center">
                            <p className="text-xs text-gray-500 mb-1">Total Calls</p>
                            <p className="text-lg font-bold text-gray-900">{user.totalCalls}</p>
                            <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                              <div
                                className="bg-primary-500 h-1.5 rounded-full transition-all"
                                style={{ width: `${getProgressWidth(user.totalCalls, maxCalls)}%` }}
                              />
                            </div>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-500 mb-1">Connected</p>
                            <p className="text-lg font-bold text-green-600">{user.connectedCalls}</p>
                            <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                              <div
                                className="bg-green-500 h-1.5 rounded-full transition-all"
                                style={{ width: `${getProgressWidth(user.connectedCalls, maxConnected)}%` }}
                              />
                            </div>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-500 mb-1">Rate</p>
                            <p className="text-lg font-bold text-blue-600">{user.connectionRate}%</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-500 mb-1">Duration</p>
                            <p className="text-lg font-bold text-purple-600">{user.totalDurationFormatted}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-500 mb-1">Avg Call</p>
                            <p className="text-lg font-bold text-indigo-600">{user.avgDurationFormatted}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-500 mb-1">Converted</p>
                            <p className="text-lg font-bold text-amber-600">{user.conversionCount}</p>
                          </div>
                        </div>
                      </div>

                      {/* Call Breakdown */}
                      <div className="mt-3 flex flex-wrap gap-2 text-xs">
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full">
                          {user.connectedCalls} Connected
                        </span>
                        <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full">
                          {user.missedCalls} Missed
                        </span>
                        <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full">
                          {user.busyCalls} Busy
                        </span>
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full">
                          {user.notConnectedCalls} Not Connected
                        </span>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Performance Tips */}
        {leaderboard.length > 0 && (
          <Card padding="p-4" className="bg-blue-50 border border-blue-100">
            <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
              <HiOutlineTrendingUp className="w-5 h-5" />
              Performance Tips
            </h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>Higher call volumes generally lead to better conversion rates</li>
              <li>Focus on improving connection rates by calling at optimal times</li>
              <li>Average call duration can indicate call quality and engagement</li>
              <li>Track your missed calls and follow up with those leads</li>
            </ul>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default Leaderboard;